import { Routes, Route, useLocation } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import Layout from './components/Layout'
import Home from './pages/Home'
import Collections from './pages/Collections'
import CollectionProducts from './pages/CollectionProducts'
import ProductDetails from './pages/ProductDetails'
import Checkout from './pages/Checkout'
import Contact from './pages/Contact'
import About from './pages/About'
import Sale from './pages/Sale'
import Category from './pages/Category'
import Maintenance from './pages/Maintenance'
import AdsLanding from './pages/AdsLanding'
import AdsSuccess from './pages/AdsSuccess'
import OrderSuccess from './pages/OrderSuccess'
import AdminLogin from './pages/admin/AdminLogin'
import AdminDashboard from './pages/admin/AdminDashboard'
import AdminProducts from './pages/admin/AdminProducts'
import AdminCollections from './pages/admin/AdminCollections'
import AdminOrders from './pages/admin/AdminOrders'
import AdminAnalytics from './pages/admin/AdminAnalytics'
import AdminSettings from './pages/admin/AdminSettings'
import ProtectedRoute from './components/ProtectedRoute'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { CartProvider } from './contexts/CartContext'
import { useEffect, useState } from 'react'

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // Target all possible scrollable roots across browsers
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const { isAuthenticated, loading } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Check maintenance mode from localStorage
  useEffect(() => {
    const checkMaintenanceMode = () => {
      const savedSettings = localStorage.getItem('adminSettings');
      if (savedSettings) {
        try {
          const parsedSettings = JSON.parse(savedSettings);
          setMaintenanceMode(parsedSettings.maintenanceMode || false);
        } catch (error) {
          console.error('Error parsing maintenance mode:', error);
          setMaintenanceMode(false);
        }
      }
    };

    checkMaintenanceMode();
    
    // Listen for storage changes (in case settings are updated in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'adminSettings') {
        checkMaintenanceMode();
      }
    };
    
    // Listen for custom settings change event (for same-tab updates)
    const handleSettingsChange = (e: CustomEvent) => {
      const newSettings = e.detail.settings;
      setMaintenanceMode(newSettings.maintenanceMode || false);
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminSettingsChanged', handleSettingsChange as EventListener);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminSettingsChanged', handleSettingsChange as EventListener);
    };
  }, []);

  // Show maintenance page if maintenance mode is on and user is not admin
  const isAdminRoute = window.location.pathname.startsWith('/admin');
  
  if (!loading && maintenanceMode && !isAuthenticated && !isAdminRoute) {
    return <Maintenance />;
  }

  return (
    <div className="App min-h-screen bg-offwhite">
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="collections" element={<Collections />} />
          <Route path="collections/:collectionId" element={<CollectionProducts />} />
          <Route path="product/:productId" element={<ProductDetails />} />
          <Route path="sale" element={<Sale />} />
          <Route path="category/:categorySlug" element={<Category />} />
          <Route path="about" element={<About />} />
          <Route path="checkout" element={<Checkout />} />
          <Route path="contact" element={<Contact />} />
          <Route path="order-success" element={<OrderSuccess />} />
        </Route>

        {/* Ads Landing Routes (no header/footer) */}
        <Route path="/ads" element={<AdsLanding />} />
        <Route path="/ads/success" element={<AdsSuccess />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/dashboard" 
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/products" 
          element={
            <ProtectedRoute>
              <AdminProducts />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/collections" 
          element={
            <ProtectedRoute>
              <AdminCollections />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/orders" 
          element={
            <ProtectedRoute>
              <AdminOrders />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/analytics" 
          element={
            <ProtectedRoute>
              <AdminAnalytics />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/admin/settings" 
          element={
            <ProtectedRoute>
              <AdminSettings />
            </ProtectedRoute>
          } 
        />
      </Routes>
      <Toaster position="top-right" />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <AppContent />
      </CartProvider>
    </AuthProvider>
  )
}

export default App
